import React from 'react';
import styled from 'styled-components';

import { Icon, Tooltip } from '@column/column-ui-kit';

import { useNavigate } from '~/lib/navigation';
import { Flex, Text } from '~/styles';
import { formatDate, formatTime } from '~/util';

export enum TimelineEventStatus {
  Pending = 'pending',
  Success = 'success',
  Danger = 'danger',
  Hold = 'hold',
  InProgress = 'in-progress',
  Warning = 'warning',
  Returned = 'returned',
}

export interface TimelineEventProps {
  label: string;
  date?: Date | string;
  status?: TimelineEventStatus;
  icon?: React.ReactNode;
  iconColor?: string | ((theme: any) => string);
  showDate?: boolean;
  link?: { label: string; path: string };
  content?: React.ReactNode;
  additionalContent?: React.ReactNode;
}

const Event = styled.div`
  display: grid;
  align-items: start;
  grid-column-gap: 24px;
  grid-template-rows: auto;
  grid-row-gap: 8px;
  position: relative;

  > *:nth-child(3) {
    grid-column: 1 / -1;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: minmax(180px, 25%) 1fr;
    grid-template-rows: auto auto;

    > *:nth-child(3) {
      grid-column: 1 / -1;
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.wide}) {
    grid-template-columns: minmax(180px, 25%) auto 25%;
    grid-template-rows: auto;

    > *:nth-child(3) {
      grid-column: auto;
    }
  }
`;

const TimeLocal = styled.span`
  transition: opacity 0.2s ease-in-out;
  opacity: 1;
  position: absolute;
`;

const TimeUTC = styled(TimeLocal)`
  opacity: 0;
  position: relative;
`;

const TimeDisplay = styled.span`
  display: flex;
  gap: 8px;
`;

const Status = styled.div<{ status?: TimelineEventStatus; iconColor?: string | ((theme: any) => string) }>`
  min-width: 248px;
  height: 100%;
  padding: 14px 0 0;
  box-sizing: border-box;

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    padding: 14px 0;
  }

  &:after {
    content: '';
    width: 2px;
    border-radius: 1px;
    background: ${({ theme }) => theme.secondary.blendToBackground(200)};
    position: absolute;
    top: 42px;
    bottom: -9px;
    left: 9px;
  }

  ${Event}:last-child &:after {
    display: none;
  }

  h4 {
    display: flex;
    gap: 8px;
    align-items: center;
    color: ${({ theme, status }) =>
      theme.secondary.blendToBackground(status === TimelineEventStatus.Pending ? 600 : 1000)};
  }

  p {
    font-size: 12px;
    line-height: 18px;
    color: ${({ theme, status }) =>
      theme.secondary.blendToBackground(status === TimelineEventStatus.Pending ? 400 : 800)};
    margin: 0 0 16px 28px;

    &:hover {
      ${TimeLocal} {
        opacity: 0;
      }

      ${TimeUTC} {
        opacity: 1;
      }
    }

    a {
      cursor: pointer;
      display: flex;
      align-items: center;

      svg {
        --icon-color: ${({ theme }) => theme.secondary.background};
        opacity: 0;
        transform: translateX(-4px);
        transition:
          color 0.2s,
          transform 0.2s,
          opacity 0.2s;
      }

      &:hover {
        color: ${({ theme }) => theme.secondary.background};

        svg {
          opacity: 1;
          transform: translateX(0);
        }
      }
    }
  }

  svg {
    color: ${({ theme, iconColor, status }) => {
      if (iconColor) {
        return typeof iconColor === 'function' ? iconColor(theme) : iconColor;
      }
      if (status === TimelineEventStatus.Success) {
        return theme.success.background;
      }
      if (status === TimelineEventStatus.Danger || status === TimelineEventStatus.Returned) {
        return theme.danger.background;
      }
      if (status === TimelineEventStatus.Hold || status === TimelineEventStatus.Warning) {
        return theme.warning.background;
      }
      return theme.secondary.blendToBackground(600);
    }};
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

const AdditionalContentWrapper = styled.div`
  grid-column: 1 / -1;
  margin-top: -8px;
  margin-bottom: 16px;
`;

const getDefaultIcon = (status?: TimelineEventStatus): React.ReactNode => {
  if (status === TimelineEventStatus.Pending) return <Icon.CircleBlank />;
  if (status === TimelineEventStatus.Danger) return <Icon.CircleCross />;
  if (status === TimelineEventStatus.Hold) return <Icon.Warning />;
  if (status === TimelineEventStatus.InProgress) return <Icon.CirclePendingApproval />;
  if (status === TimelineEventStatus.Returned) return <Icon.CircleReturn />;
  return <Icon.CircleCheck />;
};

export const TimelineEvent: React.FC<TimelineEventProps> = ({
  label,
  date,
  status,
  icon,
  iconColor,
  showDate,
  link,
  content,
  additionalContent,
}) => {
  const navigate = useNavigate();
  const eventIcon = icon || getDefaultIcon(status);

  const dateContent = date ? (
    typeof date === 'string' ? (
      date
    ) : (
      <>
        <TimeDisplay>
          <TimeLocal>
            {formatDate(date, { timeZoneFormat: 'local' })}
            <br />
            {formatTime(date, { timeZoneFormat: 'local' })}
          </TimeLocal>
          <TimeUTC>
            {formatDate(date, { timeZoneFormat: 'utc' })}
            <br />
            {formatTime(date, { timeZoneFormat: 'utc' })}
          </TimeUTC>
        </TimeDisplay>
      </>
    )
  ) : null;

  const tooltipDateContent = date ? (
    <>
      <Text size={12} color="inherit">
        {formatDate(date, { timeZoneFormat: 'local' })}
        <br />
        {formatTime(date, { timeZoneFormat: 'local' })}
      </Text>
      <Text size={12} color="lighter">
        {formatDate(date, { timeZoneFormat: 'utc' })}
        <br />
        {formatTime(date, { timeZoneFormat: 'utc' })}
      </Text>
    </>
  ) : null;

  const StatusContent = (
    <Status status={status} iconColor={iconColor}>
      {date && !showDate ? (
        <Text as="h4" size={14} weight="medium" lineHeight="24px">
          {eventIcon}
          <Tooltip content={tooltipDateContent} placement="bottom-start" offsetArrowX={-52} offsetX={-12}>
            {link ? (
              <a onClick={() => navigate(link.path)} style={{ cursor: 'pointer' }}>
                {label}
              </a>
            ) : (
              label
            )}
          </Tooltip>
        </Text>
      ) : (
        <Text as="h4" size={14} weight="medium" lineHeight="24px">
          {eventIcon}
          {link ? (
            <a onClick={() => navigate(link.path)} style={{ cursor: 'pointer' }}>
              {label}
            </a>
          ) : (
            label
          )}
        </Text>
      )}

      {date && showDate && (
        <Text as="p" size={12} color="lighter">
          {dateContent}
        </Text>
      )}

      {link && (
        <Text as="p" size={12} color="lighter">
          <a onClick={() => navigate(link.path)}>
            {link.label}
            <Icon.ChevronRight />
          </a>
        </Text>
      )}
    </Status>
  );

  return (
    <Event>
      {StatusContent}
      <ContentWrapper>
        <Flex gap={8} flexDirection="column">
          {content}
        </Flex>
      </ContentWrapper>
      {additionalContent && <AdditionalContentWrapper>{additionalContent}</AdditionalContentWrapper>}
    </Event>
  );
};
